import apiClient from './client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorklogCategory {
  id: string;
  name: string;
  isBillable: boolean;
  color: string;
  displayOrder: number;
  isActive: boolean;
  isDefault: boolean;
}

export interface CreateWorklogCategoryPayload {
  name: string;
  isBillable: boolean;
  color: string;
  displayOrder?: number;
  isDefault?: boolean;
}

export interface UpdateWorklogCategoryPayload {
  name?: string;
  isBillable?: boolean;
  color?: string;
  displayOrder?: number;
  isActive?: boolean;
  isDefault?: boolean;
}

// ─── Normalizer ──────────────────────────────────────────────────────────────

function normalizeCategory(raw: Record<string, unknown>): WorklogCategory {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    isBillable: Boolean(raw.isBillable ?? raw.is_billable ?? false),
    color: String(raw.color ?? '#808080'),
    displayOrder: Number(raw.displayOrder ?? raw.display_order ?? 0),
    isActive: Boolean(raw.isActive ?? raw.is_active ?? true),
    isDefault: Boolean(raw.isDefault ?? raw.is_default ?? false),
  };
}

// ─── API calls ────────────────────────────────────────────────────────────────

/** Fetch all worklog categories for the current tenant. */
export async function getWorklogCategories(
  includeInactive = false,
): Promise<WorklogCategory[]> {
  const { data } = await apiClient.get<Record<string, unknown>[]>(
    '/worklog-categories',
    { params: includeInactive ? { includeInactive: 'true' } : undefined },
  );
  return data.map(normalizeCategory);
}

/** Create a new worklog category. */
export async function createWorklogCategory(
  payload: CreateWorklogCategoryPayload,
): Promise<WorklogCategory> {
  const { data } = await apiClient.post<Record<string, unknown>>(
    '/worklog-categories',
    payload,
  );
  return normalizeCategory(data);
}

/** Update a worklog category. */
export async function updateWorklogCategory(
  id: string,
  payload: UpdateWorklogCategoryPayload,
): Promise<WorklogCategory> {
  const { data } = await apiClient.patch<Record<string, unknown>>(
    `/worklog-categories/${id}`,
    payload,
  );
  return normalizeCategory(data);
}

/** Reorder worklog categories. */
export async function reorderWorklogCategories(
  orderedIds: string[],
): Promise<WorklogCategory[]> {
  const { data } = await apiClient.patch<Record<string, unknown>[]>(
    '/worklog-categories/reorder',
    { orderedIds },
  );
  return data.map(normalizeCategory);
}
